<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/notifications/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <data-table
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    class="mb-auto ml-12"
    :busy="loading"
    :items="records"
    :columns="columns"
    :visible-columns="visibleColumns"
  />
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="ts" setup>
import { ref, reactive, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { enumOptionsProvider, useProvider } from '@utils/table/hooks/provider';
import type { Filter } from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import { format } from 'date-fns';
import type { HydraCollection, Notification, Driver } from '@/entities/Entities';
import Export from '@utils/toolbar/panes/Export.vue';
import { useQueryRef } from '@/hooks/url';
import {useTableFields} from '@hooks/table-configuration';

interface Props {
  modelValue: Driver;
}

const props = defineProps<Props>();

const DriverNeedsLoading = computed(() => !props.modelValue);

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  driver: props.modelValue?.id ?? '',
}));
const totalFilter = computed(() => ({ ...fixedFilter.value, ...filter.value }));

const sortKey = useQueryRef<string | undefined>('sortKey', 'sentAt');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.notificationsFromDriver', {
    driver: props.modelValue?.user?.fullName,
  });
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: false,
    group: t('pages.driver.notificationList.fieldGroups.general'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'notificationPlan.name',
    title: t('notification.planName'),
    sortable: true,
    visible: true,
    group: t('pages.driver.notificationList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.driver.notificationList.filterGroups.general'),
      options: {
        label: t('notification.planName'),
      },
    },
  },
  {
    key: 'status',
    csvTranslations: {
      sent: t('notification.status.sent'),
      unknown: t('notification.status.unknown'),
      error: t('notification.status.error'),
    },
    title: t('notification.status.DEFAULT'),
    sortable: true,
    visible: true,
    formatter: (item: string) => t(`notification.status.${item}`),
    group: t('pages.driver.notificationList.fieldGroups.general'),
    filter: {
      type: 'dropdown',
      group: t('pages.driver.notificationList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/NotificationStatus', {}, (value) => ({
          value,
          label: t(`notification.status.${value}`),
        })),
        label: t('notification.status.DEFAULT'),
      },
    },
  },
  {
    key: 'transport',
    csvTranslations: {
      email: t('notification.transport.email'),
      sms: t('notification.transport.sms'),
      push: t('notification.transport.push'),
    },
    title: t('notification.transport.DEFAULT'),
    sortable: true,
    visible: true,
    formatter: (item: string) => t(`notification.transport.${item}`),
    group: t('pages.driver.notificationList.fieldGroups.general'),
    filter: {
      type: 'dropdown',
      group: t('pages.driver.notificationList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/NotificationTransport', {}, (value) => ({
          value,
          label: t(`notification.transport.${value}`),
        })),
        label: t('notification.transport.DEFAULT'),
      },
    },
  },
  {
    key: 'scheduledAt',
    title: t('notification.scheduledAt'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.driver.notificationList.fieldGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.driver.notificationList.filterGroups.general'),
      options: {
        label: t('notification.scheduledAt'),
      },
    },
  },
  {
    key: 'sentAt',
    title: t('notification.sentAt'),
    sortable: true,
    visible: true,
    formatter: (v: string | null) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.driver.notificationList.fieldGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.driver.notificationList.filterGroups.general'),
      options: {
        label: t('notification.sentAt'),
      },
    },
  },
  {
    key: 'openedAt',
    title: t('notification.openedAt'),
    sortable: true,
    visible: true,
    formatter: (v: string | null) =>
      v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : t('unknown'),
    group: t('pages.driver.notificationList.fieldGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.driver.notificationList.filterGroups.general'),
      options: {
        label: t('notification.openedAt'),
      },
    },
  },
  {
    key: 'title',
    title: t('notification.title'),
    sortable: true,
    visible: true,
    group: t('pages.driver.notificationList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.driver.notificationList.filterGroups.general'),
      options: {
        label: t('notification.title'),
      },
    },
  },
  {
    key: 'message',
    title: t('notification.message'),
    sortable: true,
    visible: true,
    computed: (item: Notification) => (item.transport === 'email' ? t('emailContent') : item.message),
    isHtml: true,
    group: t('pages.driver.notificationList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.driver.notificationList.filterGroups.general'),
      options: {
        label: t('notification.message'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: items,
  loading,
  refetch,
} = useProvider<HydraCollection<Notification>>('/notifications', providerParams, {
  disabled: DriverNeedsLoading,
});

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
.medium {
  width: 300px;
  height: auto;
}
</style>
